import { z } from 'zod';
import { BriefKind } from './brief-kind.js';
import { BriefItem } from './brief-item.js';
/**
 * Calendar strip entry — preserved from the legacy `Briefing` schema for
 * UI continuity. The today.tsx "Your day" calendar row reads these. Each
 * entry is a calendar event with optional prep marker for items the
 * agent has prepared the user for.
 */
export const BriefCalendarEntry = z.object({
    /** Stable event id from the calendar source. */
    id: z.string(),
    /** Event title shown in the strip. */
    title: z.string().max(280),
    /** ISO timestamp the event starts. */
    startsAt: z.string().datetime(),
    /** ISO timestamp the event ends. */
    endsAt: z.string().datetime(),
    /** Whether the agent has prepped a brief for this meeting. */
    prepped: z.boolean().default(false),
    /** Optional ref into the brief's items if prep produced a BriefItem. */
    itemId: z.string().optional(),
});
/** Per-surface delivery record. */
export const BriefDeliverySurface = z.enum(['web', 'slack', 'email', 'ios']);
/**
 * Per-surface server-side delivery record (e.g. "the brief was emailed
 * to the user at this timestamp"). NOT the read-state signal — the
 * brief is immutable post-create so this array is server-only, and the
 * archive `opened` pill is derived from the action ledger
 * (`action: 'open'`, `actor: 'user'`), not from `delivered_at[]`.
 */
export const BriefDelivery = z.object({
    surface: BriefDeliverySurface,
    ts: z.string().datetime(),
});
/** Scope of a pinned note relative to a brief. */
export const BriefNoteScope = z.enum(['brief', 'item']);
/** Kind of payload attached to a brief note. */
export const BriefNoteKind = z.enum(['text', 'voice', 'photo', 'file']);
/**
 * Per-brief note. `scope: 'brief'` = pinned to whole brief; `scope: 'item'`
 * = pinned to specific item via `item_id`.
 */
export const BriefNote = z.object({
    id: z.string(),
    scope: BriefNoteScope,
    brief_id: z.string(),
    item_id: z.string().optional(),
    kind: BriefNoteKind,
    body: z.string().max(5000),
    words: z.number().int().nonnegative().optional(),
    createdAt: z.string().datetime(),
    createdByUid: z.string(),
});
/**
 * Brief — one entity per generation pass, plural deliveries per surface.
 *
 * Per #166 Phase 1 + Brief-as-Dossier Spec:
 * - `brief_id` is the lever — every downstream tool action carries it
 *   in BOTH the target system's audit log AND this brief's
 *   `action_ledger`. Double-write is intentional.
 * - The brief is IMMUTABLE after delivery. Corrections live in the
 *   action_ledger (per spec §06 Q3 + user direction).
 * - Retention: paid = forever (`expiresAt` null), trial = 90d
 *   (`expiresAt: createdAt + 90d`). Firestore TTL field-override fires
 *   on `expiresAt` when present (per spec §06 Q1).
 * - `owner_user_id` is nullable: null = team brief (org-scoped),
 *   string = per-user brief (per spec §06 Q2).
 * - No `regenerations[]` field — v1 deliberately omits regeneration
 *   semantics (per spec §06 Q5).
 */
export const Brief = z.object({
    brief_id: z.string(), // UUID/slug e.g. 'brf_2026w20_mon_a3f1'
    orgId: z.string(),
    kind: BriefKind,
    owner_user_id: z.string().nullable(), // null = team brief
    generated_at: z.string().datetime(),
    delivered_at: z.array(BriefDelivery).default([]),
    source_snapshot_id: z.string(), // ref to briefSourceSnapshots/{id}
    items: z.array(BriefItem).max(20).default([]), // ordered by synthesis ranker
    agent_reasoning: z.string().max(10000).optional(), // numbered reasoning trace markdown
    /**
     * AI-drafted one-line headline summarizing the brief. 8–14 words.
     * Used as the brief-detail page title, the archive row headline, and
     * the "what's in today's brief" hover affordance. Optional because
     * the doc can exist before the headline synthesis pass completes
     * (or the call fell back to no-LLM — better to render `<AIHeadline>`
     * skeleton than a fabricated string).
     */
    headline: z.string().max(400).optional(),
    /** Calendar strip preserved from the legacy schema. Rendered in the
     *  today.tsx "Your day" row. Orthogonal to `items[]` — calendar entries
     *  are metadata about the day, not action-requiring brief items. */
    calendar_strip: z.array(BriefCalendarEntry).max(20).default([]),
    /** Scorecard measurable ids for the glance row. Preserved from the
     *  legacy schema (`scorecardGlanceIds`); renders as a 4-tile glance
     *  in the today.tsx hero header. */
    scorecard_glance_ids: z.array(z.string()).max(4).default([]),
    /**
     * Notes pinned to this brief (or its items). Lives inline on the brief
     * doc for cheap read; the canonical write path is via NOTE.CREATE
     * action-ledger events + a separate briefNotes/{noteId} collection
     * the wave 2 backend will set up.
     */
    notes: z.array(BriefNote).default([]),
    share_link: z.string().nullable().default(null), // 'leanopx.com/share/<id>_pub_<token>'
    /** Firestore TTL field. null = forever; ISO datetime past = delete. */
    expiresAt: z.string().datetime().nullable().default(null),
});
/** Wire shape for `GET /api/orgs/:o/briefs/:briefId`. */
export const ReadBriefResponse = Brief;
/**
 * Wire shape for `GET /api/orgs/:o/briefs` — archive list. The full
 * Brief docs are denormalized into a compact list shape (no full
 * agent_reasoning, no notes — those load lazily on detail).
 */
export const BriefArchiveItem = z.object({
    brief_id: z.string(),
    kind: BriefKind,
    generated_at: z.string().datetime(),
    /** Synthesized one-line headline summarizing the most-decisive item. */
    headline: z.string().max(280),
    /** Disposition counts for the archive row pills. */
    acted: z.number().int().nonnegative().default(0),
    declined: z.number().int().nonnegative().default(0),
    deferred: z.number().int().nonnegative().default(0),
    items_count: z.number().int().nonnegative().default(0),
    /** Whether the brief was opened on any surface. */
    opened: z.boolean().default(false),
});
export const ListBriefsResponse = z.object({
    briefs: z.array(BriefArchiveItem),
});
